import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { pushEvent } from '@/lib/analytics'
import {
  generateQuotePdf,
  getQuotePdfLatestSignedUrl,
  getQuotePdfVersionSignedUrl,
  listQuotePdfVersions,
} from '@/requests/quotes'

export function useQuotePdfVersionsQuery(quoteId: string) {
  return useQuery({
    queryKey: ['quotes', quoteId, 'pdf-versions'],
    queryFn: () => listQuotePdfVersions(quoteId),
    enabled: !!quoteId,
  })
}

export function useGenerateQuotePdfMutation(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await generateQuotePdf(quoteId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['quotes', quoteId, 'pdf-versions'],
      })
    },
  })
}

export function useQuotePdfSignedUrlMutation() {
  return useMutation({
    mutationFn: async ({
      quoteId,
      versionId,
    }: {
      quoteId: string
      versionId?: string
    }) => {
      const result = versionId
        ? await getQuotePdfVersionSignedUrl(quoteId, versionId)
        : await getQuotePdfLatestSignedUrl(quoteId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: () => pushEvent('pdf_downloaded', { entity: 'quote' }),
  })
}
